import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { ExternalLink, Building, Factory, Scale, Microscope } from 'lucide-react';
import { sourcesList } from '../data/translations';

const Sources = () => {
  const { language, t } = useLanguage();

  const categoryIcons = {
    construction: Building,
    industry: Factory,
    legislation: Scale,
    research: Microscope
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="sources-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('sources.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="sources-title">
              {t('sources.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl">
              {t('sources.subtitle')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Sources by Category */}
      <section className="py-20 bg-slate-50" data-testid="sources-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="space-y-12" staggerDelay={0.15}>
            {sourcesList.map((categoryData) => {
              const IconComponent = categoryIcons[categoryData.category];
              return (
                <StaggerItem key={categoryData.category}>
                  <div data-testid={`sources-category-${categoryData.category}`}>
                    {/* Category Header */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-navy flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-xl font-bold text-navy">
                        {t(`sources.categories.${categoryData.category}`)}
                      </h2>
                    </div>
                    
                    {/* Sources Grid */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {categoryData.sources.map((source, index) => (
                        <a
                          key={index}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white border border-slate-200 p-6 hover:border-[#0066CC] transition-colors group"
                          data-testid={`source-${categoryData.category}-${index}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="font-semibold text-navy group-hover:text-[#0066CC] transition-colors mb-1">
                                {source.name}
                              </h3>
                              <p className="text-sm text-slate-500">
                                {source.desc[language]}
                              </p>
                            </div>
                            <ExternalLink className="w-4 h-4 flex-shrink-0 text-slate-400 group-hover:text-[#0066CC] transition-colors" />
                          </div>
                          <p className="text-xs text-slate-400 mt-3 truncate">
                            {source.url}
                          </p>
                        </a>
                      ))}
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="border-l-4 border-[#0066CC] pl-6">
              <p className="text-sm text-slate-500 leading-relaxed">
                {language === 'ro' 
                  ? 'Toate informațiile prezentate în acest briefing provin din surse publice oficiale. Linkurile sunt furnizate pentru verificare și documentare suplimentară.'
                  : 'All information presented in this briefing comes from official public sources. Links are provided for verification and additional documentation.'
                }
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>
    </Layout>
  );
};

export default Sources;
